import { ArrowRight } from "lucide-react";
import { clickCta } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { VerticalMarquee } from "@/components/ui/vertical-marquee";
import { cn } from "@/lib/utils";
import diabloImage from "@/assets/heritage/diablo.jpg";
const avatarImage = { url: "/heritage/avatar-new.webp" };
const cocacolaImage = { url: "/heritage/cocacola.webp" };
const deadmau5Image = { url: "/heritage/deadmau5.webp" };
import diezelImage from "@/assets/heritage/diezel.jpg";
import f150Image from "@/assets/heritage/f150.webp";
const starfieldImage = { url: "/heritage/starfield-new.webp" };
const maingearImage = { url: "/heritage/maingear.webp" };

interface BrandHeritageSectionProps {
  onCtaClick?: () => void;
  showDivider?: boolean;
  className?: string;
  dividerClassName?: string;
}

const COLUMN_A = [cocacolaImage.url, diabloImage, deadmau5Image.url, starfieldImage.url];
const COLUMN_B = [avatarImage.url, f150Image, diezelImage, maingearImage.url];

function Tile({ src, alt }: { src: string; alt: string }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-border/60 bg-card/60 shadow-sm">
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="aspect-square h-auto w-full object-cover"
      />
    </div>
  );
}

function Ctas() {
  return (
    <>
      <Button
        asChild
        className="h-12 rounded-full bg-gradient-to-r from-brand-blue to-[#ec4899] px-7 font-semibold text-white hover:opacity-90"
      >
        <a href="#work">
          See The Websites I Build
          <ArrowRight className="ml-1 h-4 w-4" />
        </a>
      </Button>
      <a
        href="#contact"
        onClick={() => clickCta("Book a Meeting", "brand_heritage")}
        className="inline-flex h-12 items-center rounded-full border border-border bg-background px-6 text-sm font-semibold text-foreground transition-colors hover:bg-accent"
      >
        Book a Meeting
      </a>
    </>
  );
}

export function BrandHeritageSection({
  onCtaClick: _onCtaClick,
  showDivider = true,
  className,
  dividerClassName,
}: BrandHeritageSectionProps = {}) {
  return (
    <section
      id="brand-heritage"
      className={cn(
        "relative isolate overflow-visible bg-background -mt-2 pt-10 pb-10 sm:mt-0 sm:pt-14 sm:pb-14",
        className
      )}
    >
      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-50"
        style={{
          backgroundImage:
            "radial-gradient(ellipse at top, color-mix(in oklab, var(--brand-blue) 8%, transparent), transparent 60%)",
          maskImage: "linear-gradient(to bottom, transparent 0%, black 100px, black 100%)",
          WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, black 100px, black 100%)",
        }}
      />

      <div className="mx-auto -mt-3 max-w-7xl px-6 sm:-mt-4 lg:px-10">

        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
          {/* Left: copy */}
          <div className="text-left">
            <h2 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-5xl">
              I've designed for the{" "}
              <span className="text-gradient-cycle">brands you know.</span>
            </h2>
            <p className="mx-auto mt-5 max-w-3xl text-lg leading-relaxed text-muted-foreground lg:mx-0">
              Creative Director at MAINGEAR Computers for over a decade, leading co-branded launches with Coca-Cola, Blizzard, Ubisoft, Disney, Bethesda, and major recording artists. I bring that same level of polish to every site I build for you.
            </p>
            <div className="mt-8 hidden flex-wrap justify-center gap-3 lg:flex lg:justify-start">
              <Ctas />
            </div>
          </div>

          {/* Right: vertical marquee */}
          <div className="relative h-[380px] w-full overflow-hidden sm:h-[480px]">
            <div className="grid h-full grid-cols-2 gap-4">
              <VerticalMarquee speed={35}>
                {COLUMN_A.map((src, i) => (
                  <Tile key={`a-${i}`} src={src} alt="" />
                ))}
              </VerticalMarquee>
              <VerticalMarquee speed={35} reverse>
                {COLUMN_B.map((src, i) => (
                  <Tile key={`b-${i}`} src={src} alt="" />
                ))}
              </VerticalMarquee>
            </div>
          </div>

          {/* Mobile: CTAs below the marquee */}
          <div className="-mt-4 flex flex-wrap justify-center gap-3 lg:hidden">
            <Ctas />
          </div>
        </div>
      </div>


      {showDivider && (
        <div className={cn("mx-auto max-w-7xl px-6 pt-20 pb-14 sm:pt-28 sm:pb-20 lg:px-10", dividerClassName)}>
          <div className="h-px w-full bg-gradient-to-r from-transparent via-foreground/15 to-transparent" />
        </div>
      )}
    </section>
  );
}

